import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import ReactGA from 'react-ga4'

const SITE_NAME = 'ELN Micro-Credentials'

// on these routes the page view waits for the page to call usePageTitle with
// its real title (e.g. the MC name), so GA doesn't log the generic default
const deferredPageViewRoutes = [
    '/microcredential/:mc_key/*',
]

// if a deferred page never provides a title (load error, redirect), send the
// page view with the default title after this long
const DEFERRED_PAGE_VIEW_TIMEOUT = 5000

let pendingPath = null
let pendingTimer = null

// default title for each route, checked in order so put the more specific
// paths first. App.jsx applies these on every route change
const routeTitles = [
    ['/', 'Welcome'],
    ['/sso', 'Signing In'],
    ['/signinwithlink', 'Signing In'],
    ['/logout', 'Logged Out'],
    ['/loading', 'Loading'],
    ['/profile', 'Profile'],
    ['/home', 'Dashboard'],
    ['/home/mymicrocredentials', 'My Micro-Credentials'],
    ['/home/cohorts/*', 'Cohorts'],
    ['/home/evaluating/*', 'Evaluating'],
    ['/home/mybadges/*', 'My Badges'],
    ['/microcredential/:mc_key', 'Micro-Credential'],
    ['/microcredential/:mc_key/materials', 'Micro-Credential Materials'],
    ['/microcredential/:mc_key/precheck', 'Micro-Credential Precheck'],
    ['/microcredential/:mc_key/portfolio/*', 'Micro-Credential Portfolio'],
    ['/evaluate/:user_mc_keys/*', 'Evaluate'],
    ['/admin', 'Admin - Manage MCs'],
    ['/admin/createmc', 'Admin - Create MC'],
    ['/admin/editmc/:mcId', 'Admin - Edit MC'],
    ['/admin/viewmcdata/:mcId', 'Admin - MC Data'],
    ['/admin/users/*', 'Admin - Users'],
    ['/admin/createuser', 'Admin - Create User'],
    ['/admin/edituser/:userId', 'Admin - Edit User'],
    ['/admin/userdata/:userId', 'Admin - User Data'],
    ['/admin/data', 'Admin - Data'],
    ['/admin/cohorts/*', 'Admin - Cohorts'],
    ['/admin/progress/:progressId', 'Admin - Progress'],
    ['/admin/activity', 'Admin - Activity'],
    ['/admin/pendingmcs', 'Admin - Pending MCs'],
    ['/admin/apikeys', 'Admin - API Keys'],
    ['/admin/settings', 'Admin - Settings'],
    ['/admin/*', 'Admin'],
    ['/printable/:progressId', 'Printable Portfolio'],
    ['/changelog', 'Changelog'],
    ['/404', 'Page Not Found'],
]

export function getPageTitle(pathname) {
    const match = routeTitles.find(([path]) => matchPath(path, pathname))
    return match ? match[1] : null
}

// pass null/undefined to reset to just the site name
export function setPageTitle(title) {
    document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME
}

function sendPageView(pathname) {
    pendingPath = null
    clearTimeout(pendingTimer)
    if (!ReactGA.isInitialized) {return;}
    // path only: sign-in links carry one-time codes in the query string
    ReactGA.send({
        hitType: 'pageview',
        page: pathname,
        title: document.title,
        page_location: window.location.origin + pathname,
    })
}

function isDeferred(pathname) {
    return deferredPageViewRoutes.some(path => matchPath(path, pathname))
}

// App.jsx calls this in a layout effect on every route change, which runs
// before any page's useEffect, so usePageTitle always sees the pending path
export function startPageView(pathname) {
    clearTimeout(pendingTimer)
    setPageTitle(getPageTitle(pathname))
    pendingPath = pathname
    if (isDeferred(pathname)) {
        pendingTimer = setTimeout(() => sendPageView(pathname), DEFERRED_PAGE_VIEW_TIMEOUT)
    }
}

// App.jsx calls this in a regular effect, after pages have had a chance to
// set their own title. Deferred routes are left for usePageTitle to send
export function finishPageView(pathname) {
    if (pendingPath !== pathname || isDeferred(pathname)) {return;}
    sendPageView(pathname)
}

// override the route's default title from inside a page, e.g. with an MC name
// once it loads. Pass null until the data is ready. The first title set after
// a route change goes out with that route's GA page view
export function usePageTitle(title) {
    const { pathname } = useLocation()
    useEffect(() => {
        if (!title) {return;}
        setPageTitle(title)
        if (pendingPath === pathname) {
            sendPageView(pathname)
        }
    }, [title, pathname])
}
